import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { sortBy } from 'lodash-es';
import { ScheduleTypes } from '@frontend/api-schedule';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { ContentLoader, useModalControl } from '@frontend/design-system';
import { CalendarViewV3 } from '../../components/calendar-view/calendar-view-v3';
import { CalendarViewOffices, OfficeCalendarView } from '../../components/calendar-view/types';
import { CalendarLocationSelectorModal } from '../../components/CalendarLocationSelectorModal';
import { CalendarViewV3Header } from '../../components/CalendarViewV3Header';
import { NoEventsMessage, NoProvidersMessage } from '../../components/EmptyCalendarMessage';
import { EventCardComponent } from '../../components/schedule-calendar-components';
import { useCalendarEventV3Context } from '../../context/CalendarEventsV3Context';
import {
  useAppointmentsInfoShallowStore,
  useCalendarEventsConfigurationShallowStore,
  useGetPractitionerDetailsForCalendarV3,
  useSchedulingLocationInfo,
} from '../../hooks';
import { useGetOfficeHoursDetailsForCalendarV3 } from '../../hooks/scheduler-v3/use-get-office-hours-details-for-calendar-v3';
import { useCalendarViewV3HeaderFilterShallowStore } from '../../stores/use-calendar-view-v3-header-filter-store';
import { HeaderFilterDataType, SelectedFiltersType } from '../../types';
import { getFullName, transformV3ResponsesForReviewRequest } from '../../utils';
import { CalendarEvents } from '../Calendar/EventsPanel/CalendarEvents';
import { CalendarEventType } from '../Calendar/types';
import { isUUID } from '../Calendar/utils';
import { convertV3CalendarEventToAppointmentDetails } from './helpers';

export const CalendarV3Container = () => {
  const { t } = useTranslation('schedule');
  const { getScopeName, selectedLocationIds, getLocationTimeZone } = useSchedulingLocationInfo();
  const { setConfiguredCalendarEvent } = useCalendarEventsConfigurationShallowStore('setConfiguredCalendarEvent');
  const { setAppointments, setRefreshAppointments } = useAppointmentsInfoShallowStore(
    'setAppointments',
    'setRefreshAppointments'
  );

  const locationSelectorModalProps = useModalControl();

  const {
    isLoading,
    practitioners,
    calendarEvents,
    appointmentTypes,
    appointmentStatusList,
    isIntegratedOffice,
    calendarEventsTotalCount,
    selectedMultiLocationIds,
    hasSelectedMultiLocationIds,
    scheduleEntries,
    scheduleEntriesOfficeData,
    refetchCalendarEvents,
    setHasSelectedMultiLocationIds,
    setSelectedMultiLocationIds,
  } = useCalendarEventV3Context();

  const {
    filteredCalendarEvents,
    setFilteredCalendarEvents,
    hasActiveFilters,
    selectedFilters,
    selectedCalendarDate,
    selectedFilteredLocationIds,
    setSelectedFilteredLocationIds,
    setRefetchCalendarEvents,
  } = useCalendarViewV3HeaderFilterShallowStore(
    'filteredCalendarEvents',
    'setFilteredCalendarEvents',
    'selectedFilters',
    'hasActiveFilters',
    'selectedCalendarDate',
    'selectedFilteredLocationIds',
    'setRefetchCalendarEvents',
    'setSelectedFilteredLocationIds'
  );

  // useEffect to set refetchCalendarEvents in the store
  useEffect(() => {
    if (refetchCalendarEvents) {
      setRefetchCalendarEvents?.(refetchCalendarEvents);
    }
  }, []);

  const filteredPractitioners = useMemo(() => {
    return selectedFilters?.practitionerIds?.length
      ? practitioners.filter((practitioner) => selectedFilters.practitionerIds?.includes(practitioner.id))
      : practitioners || [];
  }, [practitioners, selectedFilters?.practitionerIds]);

  const filteredCalendarEventsData = useMemo(() => {
    const hasPractitionerIdsFilter = !!selectedFilters?.practitionerIds?.length;
    const hasAppointmentTypeFilter = !!selectedFilters?.appointmentTypeIds?.length;
    const confirmedStatusFilter = !!selectedFilters?.confirmationStatus && selectedFilters.confirmationStatus !== 'all';

    const isFilterApplied =
      hasPractitionerIdsFilter || hasAppointmentTypeFilter || confirmedStatusFilter || hasActiveFilters;

    if (isFilterApplied) {
      return filteredCalendarEvents;
    }

    return calendarEvents;
  }, [filteredCalendarEvents, calendarEvents, selectedFilters, hasActiveFilters]);

  const { getPractitionerEventsForCalendarObject } = useGetPractitionerDetailsForCalendarV3({
    calendarEvents: filteredCalendarEventsData,
    practitioners: filteredPractitioners,
    schedules: scheduleEntries,
  });

  const { getOfficeExceptionEventsForCalendarObject, getOfficeWorkingHours, getOfficeAvailabilityExceptions } =
    useGetOfficeHoursDetailsForCalendarV3({
      schedules: scheduleEntriesOfficeData,
    });

  // TODO: Review this logic to handle the configured calendar event
  const handleConfiguredCalendarEvent = (configuredCalendarEvent: CalendarEventType) => {
    const isProviderEvent = configuredCalendarEvent.providerId && isUUID(configuredCalendarEvent.providerId);
    const isOfficeEvent = configuredCalendarEvent.locationId && !configuredCalendarEvent.providerId;
    if (isOfficeEvent || isProviderEvent) {
      setConfiguredCalendarEvent(configuredCalendarEvent);
    }
  };

  const getOfficeCalendarViewObject = (locationId: string): OfficeCalendarView => {
    return {
      officeName: getScopeName(locationId),
      officeTimezone: getLocationTimeZone(locationId),
      officeExceptions: getOfficeExceptionEventsForCalendarObject(locationId),
      hasAnyWorkingHoursSet: !!scheduleEntriesOfficeData.find(
        (schedule) => schedule.locationId === locationId && schedule.id === locationId
      )?.recurrenceRules?.workingHours?.length,
      workingHours: getOfficeWorkingHours(locationId),
      availabilityExceptions: getOfficeAvailabilityExceptions(locationId),
      providers: getPractitionerEventsForCalendarObject(locationId),
      locationId: locationId,
      calendarDateValue: formatDate(selectedCalendarDate, 'YYYY-MM-DD'),
    };
  };

  const appointmentTypeListForHeader = useMemo<HeaderFilterDataType[]>(() => {
    return (
      appointmentTypes?.map((appointmentType) => ({
        label: appointmentType.externalName || appointmentType.displayName || '',
        id: appointmentType.id,
        locationId: appointmentType.locationId,
      })) || []
    );
  }, [appointmentTypes]);

  const practitionerListForHeader = useMemo<HeaderFilterDataType[]>(() => {
    return (
      practitioners?.map((practitioner) => ({
        label: getFullName(practitioner),
        id: practitioner.id,
        locationId: practitioner.locationId,
      })) || []
    );
  }, [practitioners]);

  const [defaultAppointmentTypeIds, defaultPractitionerIds] = useMemo<[string[], string[]]>(() => {
    return [
      appointmentTypeListForHeader.map((appointmentType) => appointmentType.id),
      practitionerListForHeader.map((practitioner) => practitioner.id),
    ];
  }, [appointmentTypeListForHeader, practitionerListForHeader]);

  const unconfirmedCalendarEventsCount = useMemo(() => {
    return (
      (hasActiveFilters ? filteredCalendarEventsData : calendarEvents)?.filter(
        (calendarEvent) => calendarEvent?.attendeeStatus?.toLowerCase() === 'unconfirmed'
      ).length || 0
    );
  }, [calendarEvents, filteredCalendarEventsData, hasActiveFilters]);

  const getFilteredCalendarEvents = useCallback(
    (calendarEventsData: CalendarEvent[] = [], selectedFiltersData: SelectedFiltersType) => {
      if (!calendarEventsData.length) return [];

      // Extract and normalize filters
      const { practitionerIds = [], appointmentTypeIds = [], confirmationStatus = '' } = selectedFiltersData;

      const hasPractitionerFilter = practitionerIds.length > 0;
      const hasAppointmentTypeFilter = appointmentTypeIds.length > 0;
      const hasAppointmentStatusFilter = confirmationStatus && confirmationStatus.toLowerCase() !== 'all';

      return calendarEventsData.filter((calendarEvent) => {
        // Normalize calendar event data
        const attendeeStatus = calendarEvent?.attendeeStatus?.toLowerCase().replaceAll('_', ' ') || '';
        const practitionerId = calendarEvent?.organizerId || '';
        const appointmentTypeId = calendarEvent?.referenceTypeId || '';

        // Check individual filters
        const matchesPractitioner = hasPractitionerFilter && practitionerIds.includes(practitionerId);
        const matchesAppointmentType = hasAppointmentTypeFilter && appointmentTypeIds.includes(appointmentTypeId);
        const matchesAppointmentStatus =
          hasAppointmentStatusFilter && attendeeStatus === confirmationStatus.toLowerCase().replaceAll('_', ' ');

        // Combine filters dynamically
        const filters = [
          hasPractitionerFilter ? matchesPractitioner : true,
          hasAppointmentTypeFilter ? matchesAppointmentType : true,
          hasAppointmentStatusFilter ? matchesAppointmentStatus : true,
        ];

        // Return true if all active filters match
        return filters.every(Boolean);
      });
    },
    []
  );

  // useEffect to filter calendar events based on selected filters
  useEffect(() => {
    const hasFilters =
      (!!selectedFilters?.practitionerIds?.length ||
        !!selectedFilters?.appointmentTypeIds?.length ||
        (!!selectedFilters?.confirmationStatus && selectedFilters.confirmationStatus !== 'all')) &&
      hasActiveFilters;

    if (hasFilters) {
      const filteredAppointments = getFilteredCalendarEvents(calendarEvents || [], selectedFilters);
      setFilteredCalendarEvents(filteredAppointments);
    } else {
      setFilteredCalendarEvents(calendarEvents);
    }
  }, [selectedFilters, calendarEvents, hasActiveFilters]);

  const calendarObject = useMemo<CalendarViewOffices>(() => {
    const locationIds = selectedFilteredLocationIds?.length ? selectedFilteredLocationIds : selectedMultiLocationIds;
    if (!!locationIds?.length) {
      return locationIds
        .map((locationId) => {
          return getOfficeCalendarViewObject(locationId ?? '');
        })
        .sort((a, b) => a.officeTimezone.localeCompare(b.officeTimezone));
    }
    return [getOfficeCalendarViewObject(selectedLocationIds[0])];
  }, [
    practitioners,
    calendarEvents,
    filteredCalendarEvents,
    filteredPractitioners,
    selectedMultiLocationIds,
    selectedLocationIds,
    selectedFilters,
    selectedFilteredLocationIds,
    getOfficeCalendarViewObject,
    selectedCalendarDate,
  ]);

  const handleMultiLocationSelection = useCallback((locationIds: string[]) => {
    setSelectedMultiLocationIds(locationIds);
    setHasSelectedMultiLocationIds(true);
  }, []);

  // useEffect to update selectedMultiLocationIds when selectedLocationIds changes
  useEffect(() => {
    if (selectedLocationIds && selectedLocationIds.length > 4 && !hasSelectedMultiLocationIds) {
      locationSelectorModalProps.openModal();
    } else if (selectedLocationIds && selectedLocationIds.length <= 4) {
      setSelectedMultiLocationIds(selectedLocationIds);
    }
  }, [selectedLocationIds, hasSelectedMultiLocationIds]);

  // Convert calendarEvents to appointments format to display in event cards and store in appointments store
  useEffect(() => {
    if (calendarEvents?.length > 0) {
      const appointments = convertV3CalendarEventToAppointmentDetails(calendarEvents);
      setAppointments(appointments);
    }
  }, [calendarEvents]);

  const alphabeticallySortedPractitionerList = useMemo(() => {
    const providersListData = transformV3ResponsesForReviewRequest({
      practitionerDataV3: practitioners,
    }).transformedPractitioners as unknown as ScheduleTypes.Provider[];
    return sortBy(providersListData, [(practitioner) => practitioner.lastName?.toLowerCase()]);
  }, [practitioners]);

  // Update setRefreshAppointment when loading is finished
  useEffect(() => {
    if (!isLoading) {
      setRefreshAppointments(refetchCalendarEvents);
    }
  }, [isLoading]);

  return (
    <>
      <section css={containerStyles} id='calendar-view-wrapper'>
        <CalendarViewV3Header
          selectedMultiLocationIds={selectedMultiLocationIds}
          appointmentTypeList={appointmentTypeListForHeader}
          practitionersList={practitionerListForHeader}
          unconfirmedCalendarEventsCount={unconfirmedCalendarEventsCount}
          defaultAppointmentTypeIds={defaultAppointmentTypeIds}
          defaultPractitionerIds={defaultPractitionerIds}
          calendarEventsCount={hasActiveFilters ? filteredCalendarEventsData?.length : calendarEventsTotalCount || 0}
          selectedFilteredLocationIds={selectedFilteredLocationIds}
          setSelectedFilteredLocationIds={setSelectedFilteredLocationIds}
          appointmentStatusList={appointmentStatusList}
        />

        <div css={{ position: 'relative', overflow: 'auto' }}>
          <ContentLoader
            show={isLoading}
            message={t('Loading appointments...')}
            css={{ justifyContent: 'flex-start', paddingTop: 'calc(50vh - 150px)' }}
          />
          <CalendarViewV3
            data={calendarObject}
            onEventCreate={(startHour, endHour, providerName, providerId, isValid, locationId, calendarDateValue) => {
              handleConfiguredCalendarEvent({
                startHour,
                endHour,
                providerName,
                providerId,
                isValid,
                locationId,
                calendarDateValue,
              });
            }}
            EventCardComponent={EventCardComponent}
            NoProviderComponent={isLoading ? undefined : isIntegratedOffice ? NoEventsMessage : NoProvidersMessage}
            shouldAllowEventCreation={!isIntegratedOffice}
            calendarDate={selectedCalendarDate}
          />
        </div>
        <CalendarEvents
          customSelectedLocationIds={selectedMultiLocationIds}
          customProvidersList={alphabeticallySortedPractitionerList}
        />
      </section>

      <CalendarLocationSelectorModal
        modalProps={locationSelectorModalProps.modalProps}
        locationIds={selectedLocationIds}
        updateSelectedLocationIds={handleMultiLocationSelection}
      />
    </>
  );
};

const containerStyles = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  container-type: inline-size;
`;
